import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs } from '@carboledger/ui';
import { MassBalancePeriodSection } from '../components/MassBalancePeriod/MassBalancePeriodSection';
import { MaterialsTable } from '../components/Materials/MaterialsTable';
import { PageHeader } from '@/components/layout';
import { ConfirmDialog } from '@/components/feedback';
import { ACCOUNT_SECTIONS, ACCOUNT_SECTION_PARAM } from '@/app/routes';
import { useCurrentUser, useTranslations } from '@/hooks';
import { CompanyProfileForm } from '../components/CompanyProfile/CompanyProfileForm';
import { CompanyStructureSection } from '../components/CompanyStructure/CompanyStructureSection';
import { useUpdateCompanyProfileMutation } from '../api/accountSetupApi';
import { toCompanyProfileForm } from '../mappers/accountSetupMappers';
import { buildAccountSetupTabs, resolveAccountSection } from '../constants/accountSetup.constants';
import styles from './AccountSetupPage.module.scss';

export const AccountSetupPage = () => {
  const t = useTranslations();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useCurrentUser();

  const [updateCompanyProfile, { isLoading: isSaving }] = useUpdateCompanyProfileMutation();

  const [isSectionDirty, setIsSectionDirty] = useState(false);
  const [pendingSection, setPendingSection] = useState(null);

  const activeSection = resolveAccountSection(searchParams.get(ACCOUNT_SECTION_PARAM));
  const tabs = useMemo(() => buildAccountSetupTabs(t), [t]);

  const openSection = useCallback(
    (section) => {
      setIsSectionDirty(false);
      setSearchParams({ [ACCOUNT_SECTION_PARAM]: section });
    },
    [setSearchParams],
  );

  const handleSectionChange = useCallback(
    (section) => {
      if (section === activeSection) return;
      if (isSectionDirty) setPendingSection(section);
      else openSection(section);
    },
    [activeSection, isSectionDirty, openSection],
  );

  const discardAndOpenPending = useCallback(() => {
    openSection(pendingSection);
    setPendingSection(null);
  }, [openSection, pendingSection]);

  const keepEditing = useCallback(() => setPendingSection(null), []);

  const companyProfileValues = useMemo(
    () => toCompanyProfileForm(user?.companyProfile),
    [user?.companyProfile],
  );

  const handleSaveCompanyProfile = useCallback(
    async (values) => {
      await updateCompanyProfile(values);
    },
    [updateCompanyProfile],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageHeader title={t('ACCOUNT_SETUP_TITLE')} />
      </div>

      <div className={styles.tabs}>
        <Tabs
          tabs={tabs}
          value={activeSection}
          onChange={handleSectionChange}
          aria-label={t('ACCOUNT_SETUP_TITLE')}
        />
      </div>

      <div className={styles.content}>
        {activeSection === ACCOUNT_SECTIONS.companyProfile && (
          <CompanyProfileForm
            initialValues={companyProfileValues}
            onSubmit={handleSaveCompanyProfile}
            isSubmitting={isSaving}
            onDirtyChange={setIsSectionDirty}
          />
        )}

        {activeSection === ACCOUNT_SECTIONS.companyStructure && <CompanyStructureSection />}

        {activeSection === ACCOUNT_SECTIONS.massBalancePeriod && <MassBalancePeriodSection />}

        {activeSection === ACCOUNT_SECTIONS.materials && <MaterialsTable />}
      </div>

      <ConfirmDialog
        open={pendingSection !== null}
        title={t('COMMON_DISCARD_TITLE')}
        bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
        description={t('COMMON_DISCARD_DESCRIPTION')}
        confirmLabel={t('COMMON_DISCARD_CHANGES')}
        tone="danger"
        onConfirm={discardAndOpenPending}
        onCancel={keepEditing}
      />
    </div>
  );
};
